import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { NavProofMenu, NavProofMobileItems } from './NavProofMenu';
import logo from '@/assets/logo.png';

const allNavLinks = [
  { label: 'Benefits', href: '/#benefits' },
  { label: 'How it Works', href: '/#process' },
  { label: 'Demo', href: '/#interactive-demo' },
  { label: 'Resources', href: '__proof__', isProofMenu: true },
  { label: 'Features', href: '/#differentiator' },
  { label: 'Pricing', href: '/pricing', isRoute: true, hideForPro: true },
];

export function NavigationBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isSubscribed, currentTier } = useSubscription();

  const isPro = isSubscribed && currentTier.id !== 'free';
  const isHomePage = location.pathname === '/';

  // Filter nav links based on subscription status
  const navLinks = useMemo(() => {
    return allNavLinks.filter(link => !link.hideForPro || !isPro);
  }, [isPro]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 32);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setMobileMenuOpen(false);
    
    if (isRoute) {
      navigate(href);
      return;
    }
    
    // Handle hash links - navigate to homepage with hash if not already there
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2); // Remove '/#'
      
      if (isHomePage) {
        // Already on homepage, just scroll
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Navigate to homepage with hash
        navigate('/', { state: { scrollTo: sectionId } });
      }
    }
  };

  const handleLogoClick = () => {
    setMobileMenuOpen(false);
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-200 ease-out animate-nav-slide-down",
        scrolled 
          ? "backdrop-blur-[12px] bg-background/[0.88] border-b border-border/30 shadow-sm py-2.5 md:py-3 2xl:py-4" 
          : "backdrop-blur-[4px] bg-background/30 py-4 md:py-5 2xl:py-6"
      )}
    >
      <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-[1400px] mx-auto px-6 2xl:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={handleLogoClick}
            className="flex items-center gap-2.5 cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
             <div className="h-10 w-10 2xl:h-12 2xl:w-12 rounded-xl gradient-primary flex items-center justify-center shadow-lg overflow-hidden">
              <img src={logo} alt="Verithos AI qualitative research platform logo" width={28} height={28} className="h-6 w-6 2xl:h-7 2xl:w-7 object-contain" />
            </div>
            <span className="text-xl 2xl:text-2xl font-semibold tracking-tight">Verithos</span>
            <span className="px-1.5 py-0.5 text-[10px] 2xl:text-[11px] font-semibold uppercase tracking-wider bg-primary/10 text-primary rounded border border-primary/20">
              Beta
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 2xl:gap-2">
            {navLinks.map((link, index) => (
              link.isProofMenu ? (
                <div
                  key={link.label}
                  className="animate-nav-item-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <NavProofMenu />
                </div>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href, link.isRoute)}
                  className={cn(
                    "px-4 py-2 2xl:px-5 2xl:py-2.5 text-sm 2xl:text-base font-medium rounded-lg transition-all duration-200 animate-nav-item-fade-in",
                    "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    link.isRoute && location.pathname === link.href && "text-foreground bg-muted/50"
                  )}
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  {link.label}
                </button>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 2xl:gap-4">
            <div className="animate-nav-item-fade-in" style={{ animationDelay: '0.3s' }}>
               <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="font-medium 2xl:text-base 2xl:h-11"
              >
                Sign In
              </Button>
            </div>
            <div className="animate-nav-item-fade-in" style={{ animationDelay: '0.4s' }}>
               <Button 
                onClick={() => navigate('/auth')}
                className="gradient-primary shadow-lg glow-primary-subtle font-medium 2xl:text-base 2xl:h-11 2xl:px-6 hover:scale-[1.02] active:scale-[0.98] transition-transform"
              >
                Get Started
                 <ArrowRight className="ml-2 h-4 w-4 2xl:h-5 2xl:w-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted/50 active:scale-95 transition-transform"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay — keep framer-motion only for AnimatePresence exit */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 top-[60px] bg-background/95 backdrop-blur-sm md:hidden z-40"
                onClick={() => setMobileMenuOpen(false)}
              />
              {/* Menu Content */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="fixed left-0 right-0 top-[60px] md:hidden z-50 bg-background border-b shadow-lg"
              >
                <div className="max-w-6xl mx-auto px-6 py-4 space-y-1">
                  {navLinks.map((link, index) => (
                    link.isProofMenu ? (
                      <div
                        key={link.label}
                        className="animate-nav-item-fade-in"
                        style={{ animationDelay: `${0.05 * index}s` }}
                      >
                        <NavProofMobileItems onItemClick={() => setMobileMenuOpen(false)} />
                      </div>
                    ) : (
                      <button
                        key={link.label}
                        onClick={() => handleNavClick(link.href, link.isRoute)}
                        className="block w-full text-left px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 active:bg-muted animate-nav-item-fade-in"
                        style={{ animationDelay: `${0.05 * index}s` }}
                      >
                        {link.label}
                      </button>
                    )
                  ))}
                  <div className="pt-4 space-y-3 border-t mt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => { setMobileMenuOpen(false); navigate('/auth'); }} 
                      className="w-full h-12 text-base"
                    >
                      Sign In
                    </Button>
                    <Button 
                      onClick={() => { setMobileMenuOpen(false); navigate('/auth'); }} 
                      className="w-full h-12 text-base gradient-primary"
                    >
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
